<template>
  <li>
    <div v-if="megaMenuOptions?.enabled" 

         @mouseenter.prevent="emit('opened')"
          @mouseleave.prevent="emit('closed')"
          @keydown="onButtonKeydown"
          ref="megaMenuContainer"
    >
      <button type="button"
              @click="onMenuButtonClick"
              class="h-full flex gap-x-1 items-center whitespace-nowrap btn-link hover:cursor-pointer"
              aria-haspopup="true"
              :aria-label="`Open ${label} menu`"
              :aria-expanded="megaMenuActive"
              :aria-controls="megaMenuId"
      >
        {{ label }}
        <div v-if="tag" class="rounded-full text-sm text-center px-3 py-1 text-white"
            :style="getLinkTagStyling">
          {{tag}}
        </div>
      </button>
        <div v-show="megaMenuActive"
              :id="megaMenuId"
              role="menu"
              class="absolute left-0 top-[99%] w-screen max-h-130 min-h-fit bg-white border-b border-gray-300"
              :aria-hidden="!megaMenuActive"
        >
          <ul class="flex flex-row gap-20 pt-4 pb-12 px-12 breakpoint-driven-widths">
            <li v-for="(column, columnIndex) in megaMenuOptions.columns"
                :id="megaMenuColumnId(columnIndex)"
                :key="`mega-col-${columnIndex}`"
                :data-index="columnIndex + 1"
                class="flex flex-col"
            >
              <h3 class="mb-6 text-[1.3em] text-black" tabindex="-1">
                <NuxtLink v-if="column.headerUrl" 
                   :href="column.headerUrl" 
                    role="menuitem"
                   class="hover:text-black!"
                   external
                >
                  {{ column.header }}
                </NuxtLink>
                <template v-else>
                   {{ column.header }}
                </template>
              </h3>
              <ul class="grid grid-flow-col auto-cols-max grid-rows-[repeat(13,auto)] gap-[0.45rem_5rem]" role="menu">
                <li v-for="(link) in column.links" class="max-w-[20ch] wrap-break-word">
                  <NuxtLink :to="link.url" role="menuitem"
                            class="text-[1.1em] text-ash-100 hover:text-black!  focus:outline-offset-2 focus:outline-black"
                            external>
                    {{ link.label }}
                  </NuxtLink>
                </li>
              </ul>
            </li>

            <li v-if="!!megaMenuOptions?.featured"
                :id="megaMenuColumnId('featured')"
                :key="`mega-col-featured-${megaMenuId}`"
                :data-index="megaMenuOptions.columns.length"
                class="flex flex-col ml-auto max-w-54"
            >
              <h3 class="mb-6 text-[1.3em] text-black">{{ megaMenuOptions.featured.headline }}</h3>
              <img :src="megaMenuOptions.featured.imageUrl" :alt="megaMenuOptions.featured.imageAltText">
              <div class="mt-3 mb-5">
                <h4 class="text-[1.3em] text-black">{{ megaMenuOptions.featured.description.headline }}</h4>
                <p class="text-ash-100">{{ megaMenuOptions.featured.description.copy }}</p>
              </div>
              <a v-if="!!megaMenuOptions.featured.callToAction"
                 :href="megaMenuOptions.featured.callToAction.url"
                 class="inline-flex items-center min-h-14 w-fit text-[1.1em] text-white hover:text-white! bg-ash-500 hover:bg-ash-100 rounded-md px-[1em]"
              >
                {{ megaMenuOptions.featured.callToAction.label }}
              </a>
            </li>
          </ul>
        </div>
    </div>
  </li>
</template>

<script setup lang="ts">
import { computed, ref ,onMounted, onUnmounted} from 'vue'
import { type BuilderNavLink } from '../organism/navBar.vue';

interface DesktopNavMegaMenuEmits {
  (e: 'opened');
  (e: 'closed');
}

const {
  label,
  tag = '',
  tagTextColor = 'white',
  tagBackgroundColor = 'none',
  megaMenuOptions,
} = defineProps<BuilderNavLink>();

const emit = defineEmits<DesktopNavMegaMenuEmits>();
let lastKeyboardMenuToggleAt = 0;

const megaMenuContainer = ref();
const megaMenuActive = useElementHover(megaMenuContainer, {
  delayEnter: 125,
  delayLeave: 400
});
const megaMenuCleanLinkLabel = computed(() => label?.replaceAll(/[^a-zA-Z0-9]/g, '') ?? '');
const megaMenuId = computed(() => `mega-menu-${megaMenuCleanLinkLabel.value}`);
const megaMenuColumnId = (index) => `mega-menu-column-${megaMenuCleanLinkLabel.value}-${index}`;

function getFirstMegaMenuHeading(): HTMLElement | null {
  const root = megaMenuContainer.value as HTMLElement | undefined
  if (!root) return null
  return root.querySelector(
    'ul.breakpoint-driven-widths > li > h3[tabindex="-1"]'
  ) as HTMLElement | null
}



const openMegaMenu = () => {
  megaMenuActive.value = true;
  emit('opened');
}

function onMenuButtonClick() {
  if (Date.now() - lastKeyboardMenuToggleAt < 100) {
    return
  }
  toggleMegaMenu()
}

const closeMegaMenu = () => {
  megaMenuActive.value = false;
  emit('closed');
}

const getLinkTagStyling = computed(() => {
  return {color: tagTextColor || 'white', backgroundColor: tagBackgroundColor || 'none'};
});

defineExpose({
  isActive: megaMenuActive,
  open: openMegaMenu,
  close: closeMegaMenu
});

function toggleMegaMenu() {
  if (megaMenuActive.value) {
    closeMegaMenu()
  } else {
    openMegaMenu()
  }
}

function onButtonKeydown(event: KeyboardEvent) {
  if (!megaMenuContainer.value) return

  const button = megaMenuContainer.value.querySelector('button') as HTMLButtonElement | null
  const target = event.target as Node | null
  if (event.key === 'Tab') {
    return
  }
  if (event.key === 'Enter' || event.key === ' ') {
    if (button && target && button.contains(target)) {
      event.preventDefault()
      lastKeyboardMenuToggleAt = Date.now()
      toggleMegaMenu()
    }
    return
  }

  if (event.key === 'Escape') {
    if (!megaMenuActive.value) return
    event.preventDefault()
    closeMegaMenu()
    button?.focus()
    return
  }

  const links = megaMenuContainer.value.querySelectorAll(
    'a, button'
  ) as NodeListOf<HTMLAnchorElement | HTMLButtonElement>
  const activeElement = document.activeElement as HTMLAnchorElement | HTMLButtonElement | null
  const currentIndex = Array.from(links).indexOf(activeElement!)

  switch (event.key) {
    case 'ArrowDown': {
      if (!megaMenuActive.value) {
        if (button && target && button.contains(target)) {
          event.preventDefault()
          openMegaMenu()
        }
        return
      }
      if (links.length === 0) return
      event.preventDefault()
      const nextIndex =
        currentIndex < 0 ? 0 : (currentIndex + 1) % links.length
      links[nextIndex]!.focus()
      break
    }
    case 'ArrowUp': {
      if (!megaMenuActive.value) return
      event.preventDefault()
      const heading = getFirstMegaMenuHeading()
      if (heading && activeElement === heading) {
        closeMegaMenu()
        button?.focus()
        return
      }
      if (links.length === 0) return
      if (currentIndex <= 0) {
        heading?.focus()
        return
      }
      links[currentIndex - 1]!.focus()
      break
    }
  }
}

function handleFocusOut(event: FocusEvent) {
  if (!megaMenuContainer.value) return
  const container = megaMenuContainer.value as HTMLElement
  const relatedTarget = event.relatedTarget as HTMLElement | null
  if (!relatedTarget || !container.contains(relatedTarget)) {
    closeMegaMenu()
  }
}

onMounted(() => {
  megaMenuContainer.value?.addEventListener('focusout', handleFocusOut)
})

onUnmounted(() => {
  megaMenuContainer.value?.removeEventListener('focusout', handleFocusOut)
})

</script>
